import { useState } from 'react';
import { ChevronLeft, Settings, HelpCircle, X } from 'lucide-react';
import { Portal } from '../../components/boothly/portal.jsx';
import { Z } from '../../lib/z-index.js';
import { notifySheetOpened, notifySheetClosed } from '../../widgets.jsx';

// ── OnlineStoreHeader ─────────────────────────────────────────────────────────
// Props: { title, onBack, onSettings, dark, currentRole }
// Sticky header that respects the device safe-area notch.
export function OnlineStoreHeader({ title, onBack, onSettings, dark, currentRole }) {
  const [helpOpen, setHelpOpen] = useState(false);

  const openHelp  = () => { setHelpOpen(true);  notifySheetOpened(); };
  const closeHelp = () => { setHelpOpen(false); notifySheetClosed(); };

  return (
    <>
      <header
        data-testid="app-header"
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 50,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingLeft: 20,
          paddingRight: 20,
          paddingTop: 'calc(env(safe-area-inset-top, 0px) + 12px)',
          paddingBottom: 12,
          background: dark
            ? 'rgba(18, 18, 20, 0.85)'
            : 'rgba(255, 255, 255, 0.85)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          borderBottom: dark
            ? '1px solid rgba(255, 255, 255, 0.08)'
            : '1px solid rgba(0, 0, 0, 0.08)',
        }}
      >
        {/* Left: back button + store name */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          {onBack && (
            <button
              data-testid="back-button"
              className="ghost-icon-btn"
              onClick={onBack}
              aria-label="Zurück"
              style={{ marginLeft: -8 }}
            >
              <ChevronLeft size={24} />
            </button>
          )}
          <span
            style={{
              fontSize: 17,
              fontWeight: 600,
              letterSpacing: '-0.02em',
              color: dark ? '#fff' : '#000',
              marginLeft: onBack ? 0 : 4,
            }}
          >
            {title}
          </span>
        </div>

        {/* Right: help (Helfer only) + settings */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          {currentRole === 'fulfillment' && (
            <button
              data-testid="header-help-button"
              className="ghost-icon-btn"
              onClick={openHelp}
              aria-label="Hilfe"
            >
              <HelpCircle size={22} />
            </button>
          )}
          {onSettings && (
            <button
              data-testid="settings-button"
              className="ghost-icon-btn"
              onClick={onSettings}
              aria-label="Einstellungen"
            >
              <Settings size={22} />
            </button>
          )}
        </div>
      </header>

      {/* Help-Sheet (nur im Helfer-Modus) */}
      {helpOpen && (
        <Portal>
          <div
            style={{
              position: 'fixed', inset: 0, zIndex: Z.sheetBackdrop,
              background: 'rgba(0,0,0,0.4)',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
              animation: 'fadeIn 0.2s ease',
            }}
            onClick={closeHelp}
          />
          <div
            style={{
              position: 'fixed', insetInline: 0, bottom: 0,
              zIndex: Z.sheet,
              maxHeight: '80vh',
              display: 'flex', flexDirection: 'column',
              borderRadius: '28px 28px 0 0',
              paddingBottom: 'env(safe-area-inset-bottom, 0px)',
              background: dark ? '#1C1C1E' : '#FFFFFF',
              animation: 'slideUpSheet 0.3s cubic-bezier(0.32,0.72,0,1) backwards',
            }}
          >
            {/* Handle */}
            <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 12, paddingBottom: 8 }}>
              <div className="bottom-sheet-handle" />
            </div>

            {/* Header */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '0 20px 16px',
              borderBottom: dark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)',
            }}>
              <span style={{ fontSize: 17, fontWeight: 600, color: dark ? '#fff' : '#000' }}>
                Was kann ich hier?
              </span>
              <button
                className="ghost-icon-btn"
                onClick={closeHelp}
                aria-label="Schließen"
              >
                <X size={20} />
              </button>
            </div>

            {/* Content */}
            <div style={{ overflowY: 'auto', padding: '20px 20px 32px', flex: 1 }}>
              <HelpSection
                dark={dark}
                title="Bestellungen vorbereiten"
                body="Die Liste zeigt dir Bestellungen, die versandbereit sind. Tippe auf eine Bestellung, um das Versandlabel zu öffnen und zu drucken."
              />
              <HelpSection
                dark={dark}
                title="Bestellungen scannen"
                body="Im Scanner-Tab kannst du Pakete unterwegs abscannen. Der Status wird dabei automatisch auf «Versendet» gesetzt und die Sendungsnummer erfasst."
              />
              <HelpSection
                dark={dark}
                title="Kürzlich versendet"
                body="Hier siehst du alle Pakete der letzten 7 Tage, die bereits auf dem Weg sind."
              />
            </div>
          </div>
        </Portal>
      )}
    </>
  );
}

function HelpSection({ dark, title, body }) {
  return (
    <div style={{ marginBottom: 24 }}>
      <p style={{ fontSize: 15, fontWeight: 600, color: dark ? '#fff' : '#000', marginBottom: 6 }}>
        {title}
      </p>
      <p style={{ fontSize: 15, color: dark ? 'rgba(235,235,245,0.6)' : 'rgba(60,60,67,0.6)', lineHeight: 1.5 }}>
        {body}
      </p>
    </div>
  );
}
